/**
 * Sonde de diagnostic — **exclue du build de production**.
 *
 * Expose `window.__zinc421` pour que les mesures de performance et les tests de bout
 * en bout lisent de vrais compteurs au lieu de deviner. C'est la reprise d'une leçon
 * de la v1 : son hook `jeu421-debug` est ce qui a rendu les tests déterministes
 * possibles. Et c'est la précaution que le plan impose en face (§ 5.3) : ce hook ne
 * doit pas partir en release.
 *
 * Deux conditions d'activation, jamais réunies en production :
 *   - `import.meta.env.DEV` — le serveur de développement ;
 *   - `localStorage.zinc421-debug = "1"` — pour diagnostiquer une instance déployée,
 *     à la main et en connaissance de cause.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";

import { depassements, resumer, type Compteurs } from "./budget.js";
import { coutDesJets } from "./telemetrie.js";

/** Ce que la sonde publie. Aucun code de jeu ne doit en dépendre. */
export interface ApiSonde {
  /** Compteurs du dernier rendu. */
  compteurs: () => Compteurs;
  /** Dépassements du budget § 7.3, vide si tout tient. */
  depassements: () => ReturnType<typeof depassements>;
  resume: () => string;
  /** Images par seconde mesurées sur la dernière seconde glissante. */
  fps: () => number;
  /**
   * Coût de calcul des jets : c'est le chiffre qui décidera d'un Web Worker.
   * `null` tant qu'aucun jet n'a été calculé.
   */
  coutDesJets: () => ReturnType<typeof coutDesJets>;
  /**
   * Force la perte du contexte WebGL, pour vérifier qu'on la gère. Sans ce levier,
   * l'exigence « `webglcontextlost` géré et testé » du § 7.3 resterait déclarative.
   */
  perdreContexte: () => boolean;
}

export function sondeActive(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    return globalThis.localStorage?.getItem("zinc421-debug") === "1";
  } catch {
    // Safari en navigation privée peut refuser l'accès au stockage.
    return false;
  }
}

export function Sonde() {
  const gl = useThree((s) => s.gl);
  const derniersFps = useRef(0);
  const images = useRef(0);
  const debut = useRef(0);

  useFrame(() => {
    images.current++;
    const maintenant = performance.now();
    if (debut.current === 0) debut.current = maintenant;
    const ecoule = maintenant - debut.current;
    if (ecoule >= 1000) {
      derniersFps.current = (images.current * 1000) / ecoule;
      images.current = 0;
      debut.current = maintenant;
    }
  });

  useEffect(() => {
    if (!sondeActive()) return;

    const compteurs = (): Compteurs => ({
      triangles: gl.info.render.triangles,
      appelsDeDessin: gl.info.render.calls,
      geometries: gl.info.memory.geometries,
      textures: gl.info.memory.textures,
      programmes: gl.info.programs?.length ?? 0,
      // Zéro par construction : le jet est calculé hors écran puis rejoué, il n'y a
      // plus de moteur physique dans la boucle de rendu (§ 7.1).
      corpsDynamiques: 0,
      dpr: gl.getPixelRatio(),
    });

    const api: ApiSonde = {
      compteurs,
      depassements: () => depassements(compteurs()),
      resume: () => resumer(compteurs()),
      fps: () => derniersFps.current,
      coutDesJets,
      perdreContexte: () => {
        const extension = gl.getContext().getExtension("WEBGL_lose_context");
        if (!extension) return false;
        extension.loseContext();
        return true;
      },
    };

    (globalThis as unknown as { __zinc421?: ApiSonde }).__zinc421 = api;
    return () => {
      delete (globalThis as unknown as { __zinc421?: ApiSonde }).__zinc421;
    };
  }, [gl]);

  return null;
}
